import React, { Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { HelmetProvider } from 'react-helmet-async';
import { store, persistor } from './store';
import i18n from './i18n/config';
import App from './App';
import './index.css';
import logger from './utils/logger';

function AppWrapper() {
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  useEffect(() => {
    // Wait for i18next to be initialized
    const handleInitialized = () => {
      setIsI18nInitialized(true);
    };

    // Listen for language changes
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng);
      logger.debug('Language changed in AppWrapper:', lng);
    };

    i18n.on('initialized', handleInitialized);
    i18n.on('languageChanged', handleLanguageChanged);

    // If already initialized, set state
    if (i18n.isInitialized) {
      setIsI18nInitialized(true);
    }

    return () => {
      i18n.off('initialized', handleInitialized);
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  if (!isI18nInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Force re-render when language changes by using key
  return (
    <Suspense fallback="Loading...">
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <HelmetProvider>
            <App key={`app-${currentLanguage}`} />
          </HelmetProvider>
        </PersistGate>
      </Provider>
    </Suspense>
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>
);
